import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRight, KeyRound } from 'lucide-react';
import { Brand } from '@edupra/shell';
import QRCode from 'qrcode';
import * as OTPAuth from 'otpauth';
import { platformTRPCProxy } from '@/lib/platform-trpc';
import { useAuthStore, type PlatformUser, type PlatformRole } from '@/lib/platform-auth';

type Stage =
  | { kind: 'credentials' }
  | { kind: 'totp'; ticket: string }
  | { kind: 'enroll'; ticket: string; secret: string; qrDataUrl: string; otpauthUrl: string };

/**
 * Build a fallback otpauth URI client-side if the server didn't include one.
 * Currently the platform router always returns `qrUrl`, so this is defensive.
 */
function buildOtpAuthUri(email: string, secret: string): string {
  const totp = new OTPAuth.TOTP({
    issuer: 'Edupra Platform',
    label: email,
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
    secret: OTPAuth.Secret.fromBase32(secret),
  });
  return totp.toString();
}

export function LoginPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── Stage 1: credentials ────────────────────────────────────────────────
  async function handleCredentials(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await platformTRPCProxy.auth.login.mutate({ email, password });

      if ('needsTotp' in res && res.needsTotp) {
        setStage({ kind: 'totp', ticket: res.ticket });
        setCode('');
      } else if ('enroll' in res && res.enroll) {
        const otpauthUrl =
          res.enroll.qrUrl ?? buildOtpAuthUri(email, res.enroll.secret);
        const qrDataUrl = await QRCode.toDataURL(otpauthUrl, {
          margin: 1,
          width: 220,
          color: { dark: '#0B0B0E', light: '#FFFFFF' },
        });
        setStage({
          kind: 'enroll',
          ticket: res.ticket,
          secret: res.enroll.secret,
          qrDataUrl,
          otpauthUrl,
        });
        setCode('');
      } else {
        throw new Error('Unexpected login response from platform API');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  // ── Stage 2: TOTP (verify or enroll) ────────────────────────────────────
  async function handleTotp(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let res: { token: string; user: PlatformUser };
      if (stage.kind === 'totp') {
        res = await platformTRPCProxy.auth.verifyTotp.mutate({
          ticket: stage.ticket,
          code,
        });
      } else if (stage.kind === 'enroll') {
        res = await platformTRPCProxy.auth.enrollTotp.mutate({
          ticket: stage.ticket,
          code,
        });
      } else {
        return;
      }

      useAuthStore.getState().setAuth(res.token, {
        id: res.user.id,
        email: res.user.email,
        name: res.user.name,
        role: res.user.role as PlatformRole,
      });
      navigate('/overview', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  function startOver() {
    setStage({ kind: 'credentials' });
    setCode('');
    setError('');
  }

  return (
    <div
      className="min-h-screen flex"
      style={{ background: 'var(--bg-canvas)', color: 'var(--fg-high)' }}
    >
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-between p-12"
        style={{
          background:
            'radial-gradient(60% 100% at 20% 0%, rgba(16,185,129,0.15) 0%, transparent 60%), #08080B',
          color: '#FAFAFA',
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
            maskImage:
              'radial-gradient(ellipse at 30% 20%, black 30%, transparent 75%)',
          }}
        />
        <div
          className="absolute -bottom-[20%] -right-[10%] w-[640px] h-[640px] rounded-full opacity-25 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.6) 0%, transparent 70%)' }}
        />

        <div className="relative flex items-center gap-3" style={{ color: '#FAFAFA' }}>
          <Brand size={36} weight="medium" href={null} color="#FAFAFA" accent={{ row: 3, col: 3 }} />
          <span
            className="font-mono text-[11px] uppercase"
            style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}
          >
            · admin
          </span>
        </div>

        <div className="relative max-w-md">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-3">
            Platform Console · Operator Access
          </div>
          <h1 className="text-[40px] leading-[1.04] font-semibold tracking-[-0.03em]">
            Operator sign-in.
            <br />
            <span style={{ color: 'var(--color-accent-400)' }}>2-factor required.</span>
          </h1>
          <p className="text-white/55 text-[14px] mt-5 leading-relaxed max-w-sm">
            Every platform action is audited. Use your authenticator app to confirm the
            session. Lost device? Ping a fellow owner — bootstrap is the only escape hatch.
          </p>

          <div className="mt-10 flex items-center gap-6 text-white/40 text-[12px]">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-accent-400)]" />
              Mandatory TOTP
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-accent-400)]" />
              Isolated JWT realm
            </span>
          </div>
        </div>

        <p className="relative text-[11px] text-white/30">
          © 2026 Edupra · Authorised operators only.
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-[420px] animate-rise">
          {/* Mobile-only brand */}
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <Brand size={32} weight="medium" href={null} accent={{ row: 3, col: 3 }} />
            <span
              className="font-mono text-[10px] uppercase"
              style={{ color: 'var(--fg-low)', letterSpacing: '0.08em' }}
            >
              · admin
            </span>
          </div>

          <div className="ed-eyebrow mb-3">
            {stage.kind === 'credentials' ? 'Step 1 of 2' : 'Step 2 of 2'}
          </div>
          <h2 className="text-[26px] font-semibold tracking-[-0.025em] leading-[1.15]">
            {stage.kind === 'credentials'
              ? 'Operator credentials'
              : stage.kind === 'enroll'
                ? 'Enrol your authenticator'
                : 'Verify with TOTP'}
          </h2>
          <p
            className="text-[13px] mt-2 mb-7"
            style={{ color: 'var(--fg-low)' }}
          >
            {stage.kind === 'credentials'
              ? 'Sign in with your platform-admin account. 2FA is mandatory on the next step.'
              : stage.kind === 'enroll'
                ? 'Scan this QR with Google Authenticator, 1Password, Authy, or any TOTP app — then enter the first 6-digit code.'
                : 'Enter the 6-digit code from your authenticator app.'}
          </p>

          {error && (
            <div
              className="mb-4 rounded-md px-3.5 py-2.5 text-[12px]"
              style={{
                background: 'var(--semantic-danger-bg)',
                color: 'var(--semantic-danger)',
                border:
                  '1px solid color-mix(in oklch, var(--semantic-danger) 25%, transparent)',
              }}
            >
              {error}
            </div>
          )}

          {stage.kind === 'credentials' && (
            <form onSubmit={handleCredentials} className="space-y-4">
              <div>
                <label className="ed-label">Email</label>
                <input
                  type="email"
                  className="ed-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="operator@edupra.com"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="ed-label">Password</label>
                <input
                  type="password"
                  className="ed-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="ed-btn-primary w-full"
                style={{ height: 40 }}
              >
                {loading ? (
                  'Verifying…'
                ) : (
                  <>
                    Continue <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </>
                )}
              </button>
            </form>
          )}

          {stage.kind === 'enroll' && (
            <form onSubmit={handleTotp} className="space-y-5">
              <div
                className="flex flex-col items-center gap-3 rounded-lg border p-5"
                style={{
                  borderColor: 'var(--border-line)',
                  background: 'var(--bg-surface)',
                }}
              >
                <img
                  src={stage.qrDataUrl}
                  alt="Scan this QR with your authenticator app"
                  width={220}
                  height={220}
                  className="rounded bg-white p-1"
                />
                <details className="w-full">
                  <summary
                    className="cursor-pointer text-[11px] uppercase tracking-[0.08em] text-center"
                    style={{ color: 'var(--fg-low)' }}
                  >
                    Can't scan? Enter the secret manually
                  </summary>
                  <div
                    className="mt-2 select-all rounded p-2 text-center font-mono text-[12px] tabular-nums"
                    style={{
                      background: 'var(--bg-sunken)',
                      color: 'var(--fg-high)',
                      letterSpacing: '0.04em',
                      wordBreak: 'break-all',
                    }}
                  >
                    {stage.secret}
                  </div>
                </details>
              </div>

              <div>
                <label className="ed-label">First 6-digit code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  className="ed-input font-mono tabular-nums text-center text-[18px]"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="123 456"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="ed-btn-primary w-full"
                style={{ height: 40 }}
              >
                {loading ? (
                  'Enrolling…'
                ) : (
                  <>
                    Enrol and sign in <KeyRound className="h-3.5 w-3.5" strokeWidth={2} />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={startOver}
                className="ed-btn-ghost w-full text-[12px]"
              >
                Use a different account
              </button>
            </form>
          )}

          {stage.kind === 'totp' && (
            <form onSubmit={handleTotp} className="space-y-4">
              <div>
                <label className="ed-label">Authenticator code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  className="ed-input font-mono tabular-nums text-center text-[20px]"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="123 456"
                  required
                  autoFocus
                />
                <p
                  className="mt-2 text-[11px]"
                  style={{ color: 'var(--fg-faint)' }}
                >
                  6 digits from your TOTP app.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="ed-btn-primary w-full"
                style={{ height: 40 }}
              >
                {loading ? (
                  'Verifying…'
                ) : (
                  <>
                    Sign in <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={startOver}
                className="ed-btn-ghost w-full text-[12px]"
              >
                Use a different account
              </button>
            </form>
          )}

          <p
            className="mt-8 text-center text-[12px]"
            style={{ color: 'var(--fg-low)' }}
          >
            Don't have an operator account? Run{' '}
            <code
              className="rounded px-1.5 py-0.5 font-mono text-[11px]"
              style={{ background: 'var(--bg-sunken)', color: 'var(--fg-high)' }}
            >
              pnpm admin:bootstrap
            </code>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
